import type { FC, ReactNode } from 'react'

import { Layers } from 'lucide-react'
import { formatCompactCurrency } from '@/lib/format'
import { BarChart } from '@/components/charts/bar-chart'
import { DeltaBadge } from '@/components/charts/delta-badge'
import { AnimatedNumber } from '@/components/generals/animated-number'
import { FloatingCard, PreviewFrame } from '@/components/features/preview-frame'

import {
    CHART_COLORS,
    INCOME_SERIES,
    MONTHS,
    REVENUE_METRICS,
    SUBSCRIPTION_SHARE
} from '@/constants/dashboard'

const SUBSCRIPTIONS = INCOME_SERIES.map(value => Math.round(value * SUBSCRIPTION_SHARE))
const SERVICES = INCOME_SERIES.map((value, index) => value - SUBSCRIPTIONS[index])

export const RevenuePreview: FC = (): ReactNode => {
    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex items-start justify-between gap-3'>
                    <div>
                        <p className='text-sm font-medium'>Revenue analytics</p>
                        <p className='text-[11px] text-stone'>Reconciled with billing, CRM and ledger</p>
                    </div>

                    <div className='hidden items-center gap-3 text-[11px] text-stone-dark sm:flex'>
                        <span className='flex items-center gap-1.5'><span aria-hidden='true' className='size-2 rounded-[2px] bg-burgundy' />Recurring</span>
                        <span className='flex items-center gap-1.5'><span aria-hidden='true' className='size-2 rounded-[2px] bg-gold' />Services</span>
                    </div>
                </div>

                <dl className='mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4'>
                    {REVENUE_METRICS.map(metric => (
                        <div key={metric.label} className='rounded-xl bg-ivory p-3'>
                            <dt className='truncate text-[11px] text-stone-dark'>{metric.label}</dt>

                            <dd className='mt-1.5 font-heading text-lg font-medium leading-none tracking-[-0.02em]'>
                                <AnimatedNumber value={metric.value} format={metric.format} decimals={0} />
                            </dd>

                            <dd className='mt-2'>
                                <DeltaBadge value={metric.change} />
                            </dd>
                        </div>
                    ))}
                </dl>

                <BarChart
                    title='Monthly revenue split into recurring and services revenue'
                    labels={MONTHS}
                    stacked
                    series={[
                        { name: 'Recurring', values: SUBSCRIPTIONS, color: CHART_COLORS.burgundy },
                        { name: 'Services', values: SERVICES, color: CHART_COLORS.gold }
                    ]}
                    height={160}
                    formatValue={formatCompactCurrency}
                    formatAxis={formatCompactCurrency}
                    labelEvery={2}
                    className='mt-7'
                />
            </PreviewFrame>

            <FloatingCard slow className='-bottom-7 -left-4 w-56 lg:-left-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <Layers aria-hidden='true' className='size-4 text-gold-dark' />
                    Top growth segment
                </p>

                <p className='mt-1 text-[11px] text-stone'>Mid-market accounts, up 14% in expansion revenue this quarter</p>
            </FloatingCard>
        </div>
    )
}
